import { useState } from 'react';
import { api } from '../lib/api';
import { Button, Card, Badge, FormField, Spinner } from '../components/ui';

// ---- TENANCY CHECKER ----
export function TenancyChecker() {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleCheck() {
    if (text.length < 100) { setError('Please paste a more complete agreement (at least 100 characters).'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.checkTenancy({ text });
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fade-up" style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Tenancy <em>Agreement Checker</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>Paste your tenancy agreement below. Our AI will flag illegal clauses, missing requirements, and unfair terms under UK housing law.</p>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <FormField label="Paste tenancy agreement text">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={12} placeholder="Paste the full text of your tenancy agreement here..." style={{ resize: 'vertical' }} />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>{text.length} characters</p>
          <Button onClick={handleCheck} loading={loading}>Analyse Agreement</Button>
        </div>
        {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12 }}>{error}</p>}
      </Card>

      {loading && (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Spinner />
          <p style={{ color: 'var(--ink-3)', marginTop: 12 }}>Analysing with AI — this may take a few seconds…</p>
        </Card>
      )}

      {result && (
        <div className="fade-up">
          {/* Summary */}
          <Card style={{ marginBottom: 20, borderColor: result.overall_risk === 'high' ? 'var(--danger)' : result.overall_risk === 'medium' ? 'var(--warn)' : 'var(--accent)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <h3 style={{ fontSize: 18 }}>Overall Assessment</h3>
              <Badge status={result.overall_risk} label={`${result.overall_risk} risk`} />
            </div>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6 }}>{result.summary}</p>
          </Card>

          {/* Issues */}
          {result.issues?.length > 0 ? (
            <div>
              <h3 style={{ fontSize: 16, marginBottom: 16, color: 'var(--ink-2)' }}>{result.issues.length} issue{result.issues.length !== 1 ? 's' : ''} found</h3>
              {result.issues.map((issue, i) => (
                <Card key={i} style={{ marginBottom: 12, borderLeft: `3px solid ${issue.severity === 'illegal' ? 'var(--danger)' : 'var(--warn)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <p style={{ fontSize: 14, fontWeight: 500, flex: 1, marginRight: 12 }}>"{issue.clause}"</p>
                    <Badge status={issue.severity} />
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 6 }}>{issue.explanation}</p>
                  {issue.law && <p style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>📖 {issue.law}</p>}
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ textAlign: 'center', padding: 32 }}>
              <p style={{ fontSize: 32, marginBottom: 8 }}>✅</p>
              <p style={{ fontWeight: 500 }}>No issues found</p>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>This agreement appears compliant with UK housing law</p>
            </Card>
          )}

          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 16 }}>⚠️ This is an AI-assisted guide only and does not constitute legal advice. Consult a solicitor for professional advice.</p>
        </div>
      )}
    </div>
  );
}

// ---- LICENSING CHECKER ----
export function LicensingChecker() {
  const [form, setForm] = useState({ property_type: 'single_let', bedrooms: 2, occupants: 2, households: 1, council_area: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleCheck() {
    setLoading(true);
    try {
      const res = await api.checkLicensing({ ...form, bedrooms: +form.bedrooms, occupants: +form.occupants, households: +form.households });
      setResult(res);
    } finally { setLoading(false); }
  }

  return (
    <div className="fade-up" style={{ maxWidth: 620 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Licensing <em>Checker</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>Check whether your property requires mandatory HMO licensing or is subject to local selective licensing schemes.</p>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <FormField label="Property type">
          <select value={form.property_type} onChange={set('property_type')}>
            {['single_let','hmo','flat','house'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
          </select>
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <FormField label="Bedrooms"><input type="number" min={1} value={form.bedrooms} onChange={set('bedrooms')} /></FormField>
          <FormField label="Total occupants"><input type="number" min={1} value={form.occupants} onChange={set('occupants')} /></FormField>
          <FormField label="Households"><input type="number" min={1} value={form.households} onChange={set('households')} /></FormField>
        </div>
        <FormField label="Council area (optional)">
          <input value={form.council_area} onChange={set('council_area')} placeholder="e.g. Newham, Liverpool, Salford" />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleCheck} loading={loading}>Check Licensing</Button>
        </div>
      </Card>

      {result && (
        <Card className="fade-up" style={{ borderColor: result.requires_mandatory_hmo ? 'var(--danger)' : result.may_require_additional ? 'var(--warn)' : 'var(--accent)' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {result.requires_mandatory_hmo && <Badge status="expired" label="Mandatory HMO Licence Required" />}
            {!result.requires_mandatory_hmo && result.may_require_additional && <Badge status="due_soon" label="May Require Licence" />}
            {!result.requires_mandatory_hmo && !result.may_require_additional && <Badge status="valid" label="No Mandatory Licence" />}
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 12 }}>{result.explanation}</p>
          {result.action_required && (
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <p style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Action required</p>
              <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>{result.action_required}</p>
            </div>
          )}
          {result.council_url && (
            <a href={result.council_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>
              Apply for licence at your council →
            </a>
          )}
        </Card>
      )}
    </div>
  );
}

// ---- REMINDERS ----
export function Reminders() {
  const [reminders, setReminders] = useState(null);

  useState(() => {
    api.getReminders().then(setReminders);
  }, []);

  if (!reminders) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Reminders</h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>Upcoming certificate and Right to Rent renewal alerts</p>
      </div>

      {!reminders.length ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: 32, marginBottom: 12 }}>🔔</p>
          <p style={{ fontWeight: 500, marginBottom: 4 }}>No pending reminders</p>
          <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>Reminders are automatically created when you add certificates with expiry dates</p>
        </Card>
      ) : (
        reminders.map(r => (
          <Card key={r.id} style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontWeight: 500, fontSize: 14 }}>{r.reminder_type?.replace('_', ' ')} reminder</p>
              <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Due: {new Date(r.remind_at).toLocaleDateString('en-GB')}</p>
            </div>
            <Badge status={r.reminder_type === 'overdue' ? 'expired' : 'due_soon'} label={r.reminder_type?.replace('_', ' ')} />
          </Card>
        ))
      )}
    </div>
  );
}
